<template>
  <main class="archive-tree">
    <section class="desc">
      <h1 class="title">유형별 통제 매트릭스 사전</h1>

      <p>
        본 자료는 국제내부통제협회(Internal Control Institute)에서 제공하는 내부통제 지식 프레임워크로,
        조직의 내부통제를 진단하고 개선하는 실무 도구입니다.
        조직의 특성과 리스크 수준에 맞게 선택·적용·개선함으로써
        내부통제의 성숙도를 지속적으로 향상시킬 수 있습니다.
      </p>

      <p>
        내부통제와 관련된 지식과 기술 요소를 백과사전식으로 체계화하여,
        조직이 내부통제 체계를 설계하거나 개선할 때 참고할 수 있는
        통제 활동, 절차, 기준 및 실행 가이드라인을 제공합니다.
      </p>

      <p>
        이 자료는 경영진 또는 감사·리스크 관리·내부통제 담당자가
        통제 미비 영역을 식별했을 때,
        적절한 보완 통제수단을 선정하고 적용하는 데
        유용하게 활용될 수 있습니다.
      </p>

      <h2 class="usage-guide-wrap">사용방법</h2>

      <section class="usage-guide">
        <ol>
          <li>
            <strong>① 통제 대상 프로세스 식별</strong><br />
            영역별 분류(접근 관리, 승인, 보관/보호, 제한 등),
            통제 유형별 분류(예방 통제, 적발 통제, 시정 통제) 등을 활용하여
            관련 통제 항목을 탐색합니다.
          </li>

          <li>
            <strong>② 관련 통제 항목 탐색</strong><br />
            <span>예시</span><br />
            통제명: 보안 등급 분류(Security Classification)<br />
            유형: 예방 통제<br />
            목적: 정보의 중요도를 명확히 식별할 수 있도록 보안 등급을 부여하여 분류<br />
            실행 지침: 문서의 성격과 목적에 따라 정보를 적절한 등급으로 분류<br />
            <em>
              *각 항목에는 통제 설명, 구현 방안, 피드백·입증 자료,
              장점 및 단점 등이 함께 제시됩니다.
            </em>
          </li>

          <li>
            <strong>③ 현재 운영 중인 통제와 비교</strong><br />
            통제 매트릭스 사전의 표준 통제 절차와
            조직의 현재 운영 중인 내부통제 현황을 비교하여
            누락되었거나 비효율적인 부분을 식별합니다.<br />
            갭 분석 결과를 바탕으로,
            각 통제 항목의 “구현 방안”을 참조하여
            적절한 통제 활동을 구체적으로 설계·도입합니다.<br />
            본 통제 매트릭스 사전의 모든 통제를
            일괄 적용할 필요는 없으며,
            조직의 규모, 위험 수준, 운영 특성에 따라
            선택적으로 활용할 수 있습니다.
          </li>
        </ol>
      </section>
    </section>

    <!-- ============ 선택 영역: 무료/유료 모두 사용 ============ -->
    <section aria-label="선택 영역" class="selector">
      <div class="grid">
        <!-- Generic -->
        <div class="group">
          <!-- <h3>Generic Category</h3> -->
          <h3>통제 분류</h3>
          <label v-for="g in categories.generic" :key="g.code">
            <input
              type="radio"
              name="generic"
              :value="g.code"
              v-model="selected.generic"
              @change="onPickGeneric"
            />
            <span class="code">{{ g.code }}</span><span class="label">{{ g.label }}</span>
          </label>
        </div>

        <!-- Usage -->
        <div class="group">
          <!-- <h3>Usage Activity</h3> -->
          <h3>활용 활동 영역</h3>
          <label
            v-for="u in categories.usage"
            :key="u.code"
            :class="{ disabled: !available.usage.has(u.code) }"
          >
            <input
              type="radio"
              name="usage"
              :value="u.code"
              v-model="selected.usage"
              :disabled="!available.usage.has(u.code)"
              @change="onPickUsage"
            />
            <span class="code">{{ u.code }}</span><span class="label">{{ u.label }}</span>
          </label>
        </div>

        <!-- Strength -->
        <div class="group">
          <!-- <h3>Strength of Control</h3> -->
          <h3>통제 강도</h3>
          <label
            v-for="s in categories.strength"
            :key="s.code"
            :class="{ disabled: !available.strength.has(s.code) }"
          >
            <input
              type="radio"
              name="strength"
              :value="s.code"
              v-model="selected.strength"
              :disabled="!available.strength.has(s.code)"
              @change="onPickStrength"
            />
            <span class="code">{{ s.code }}</span><span class="label">{{ s.label }}</span>
          </label>
        </div>

        <!-- Type -->
        <div class="group">
          <!-- <h3>Control Type</h3> -->
          <h3>통제 유형</h3>
          <label
            v-for="t in categories.type"
            :key="t.code"
            :class="{ disabled: !available.type.has(t.code) }"
          >
            <input
              type="radio"
              name="type"
              :value="t.code"
              v-model="selected.type"
              :disabled="!available.type.has(t.code)"
            />
            <span class="code">{{ t.code }}</span><span class="label">{{ t.label }}</span>
          </label>
        </div>
      </div>

      <!-- 조회 -->
      <div class="search-fixed">
        <button type="button" class="btn-search" @click="onSearch">조회하기</button>
        <p v-if="hint" class="hint">{{ hint }}</p>
      </div>
    </section>

    <!-- ============ 무료/비로그인 안내 + 샘플 문서(조회 후 노출) ============ -->
    <section v-if="isFree" aria-label="무료 플랜 안내">
      <p class="guard-note">
        현재 플랜에서는 샘플 문서만 확인할 수 있습니다. (문의: 070-4489-7447)
      </p>

      <!-- 조회하기 버튼을 눌렀을 때만 샘플 폼 노출 -->
      <section
        v-if="showSampleDoc"
        class="control-sheet"
        aria-labelledby="doc-title-sample"
      >
        <h1 id="doc-title-sample" class="title1">통제 설명 (Sample)</h1>

        <section class="sheet-grid" role="table" aria-label="Control description detail">
          <!-- Ref.# + NAME OF CONTROL -->
          <div class="row row--head" role="row">
            <div class="th" role="columnheader">참조 번호</div>
            <div class="td" role="cell">
              <p class="ref-code">1.0.A.P</p>
            </div>
            <div class="th" role="columnheader">통제 항목명</div>
            <div class="td" role="cell">
              <p class="name">샘플 통제 항목명</p>
            </div>
          </div>

          <!-- META -->
          <div class="row row--meta" role="row">
            <div class="th" role="columnheader">통제 분류</div>
            <div class="td" role="cell">
              <p>접근 통제</p>
            </div>

            <div class="th" role="columnheader">활용 활동 영역</div>
            <div class="td" role="cell">
              <p>권한 관리</p>
            </div>

            <div class="th" role="columnheader">통제 강도</div>
            <div class="td" role="cell">
              <p>매우 강함</p>
            </div>

            <div class="th" role="columnheader">통제 유형</div>
            <div class="td" role="cell">
              <p>사전 예방 통제</p>
            </div>
          </div>

          <!-- DESCRIPTION -->
          <div class="row" role="row">
            <div class="th th2" role="columnheader">설명</div>
            <div class="td" role="cell">
              <p>
                본 설명은 비회원 또는 무료 이용자를 위해 제공되는 예시용 통제 설명입니다.
              </p>
            </div>
          </div>

          <!-- IMPLEMENTATION SUGGESTIONS -->
          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">구현 방안</div>
            <div class="td" role="cell">
              <p>실제 적용을 가정한 예시 수준의 구현 방안을 제시합니다.</p>
            </div>
          </div>

          <!-- FEEDBACK EVIDENCE -->
          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">피드백·입증 자료</div>
            <div class="td" role="cell">
              <p>실무 환경에서 활용될 수 있는 입증 자료 예시입니다.</p>
            </div>
          </div>

          <!-- ADVANTAGES OF CONTROL -->
          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">장점</div>
            <div class="td" role="cell">
              <p>해당 통제가 제공하는 주요 장점을 요약한 내용입니다.</p>
            </div>
          </div>

          <!-- DISADVANTAGES OF CONTROL -->
          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">단점</div>
            <div class="td" role="cell">
              <p>적용 시 고려해야 할 제약 사항 및 잠재적 단점에 대한 예시입니다.</p>
            </div>
          </div>
        </section>
      </section>
    </section>

    <!-- 문서: 유료회원만 실제 데이터 노출 (여러 건) -->
    <section v-if="isPaid && showDoc && docs.length" aria-label="Control description list">
      <section
        v-for="item in docs"
        :key="item.referenceCode"
        class="control-sheet"
        :aria-labelledby="`doc-title-${item.referenceCode}`"
      >
        <h1 :id="`doc-title-${item.referenceCode}`" class="title1">통제 설명</h1>

        <section class="sheet-grid" role="table" aria-label="Control description detail">
          <div class="row row--head" role="row">
            <div class="th" role="columnheader">참조 번호</div>
            <div class="td" role="cell">
              <p class="ref-code">{{ item.referenceCode }}</p>
            </div>
            <div class="th" role="columnheader">통제 항목명</div>
            <div class="td" role="cell">
              <p class="name">{{ item.nameOfControl }}</p>
            </div>
          </div>

          <div class="row row--meta" role="row">
            <div class="th" role="columnheader">통제 분류</div>
            <div class="td" role="cell">
              <p>{{ item.genericCategory }}</p>
            </div>

            <div class="th" role="columnheader">활용 활동 영역</div>
            <div class="td" role="cell">
              <p>{{ item.usageActivity }}</p>
            </div>

            <div class="th" role="columnheader">통제 강도</div>
            <div class="td" role="cell">
              <p>{{ item.strength }}</p>
            </div>

            <div class="th" role="columnheader">통제 유형</div>
            <div class="td" role="cell">
              <p>{{ item.controlType }}</p>
            </div>
          </div>

          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">설명</div>
            <div class="td td2" role="cell">
              <p>{{ item.description }}</p>
            </div>
          </div>

          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">구현 방안</div>
            <div class="td td2" role="cell">
              <p>{{ item.implementationSuggestions }}</p>
            </div>
          </div>

          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">피드백·입증 자료</div>
            <div class="td td2" role="cell">
              <p>{{ item.feedbackEvidence }}</p>
            </div>
          </div>

          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">장점</div>
            <!-- <div class="td td2" role="cell"> -->
            <div class="td" role="cell">
              <p>{{ item.advantages }}</p>
            </div>
          </div>

          <div class="row row2" role="row">
            <div class="th th2" role="columnheader">단점</div>
            <div class="td td2" role="cell">
              <p>{{ item.disadvantages }}</p>
            </div>
          </div>
        </section>
      </section>
    </section>
  </main>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { selectLibrary, referencechildren } from '@/api/room'

/* ===== 유저/플랜 판별 ===== */
const store = useStore()
const checkUserId = computed(() => store.state.temp.userInfo)
const isLoggedIn = computed(() => !!checkUserId.value?.userId)
const isFree = computed(() => !isLoggedIn.value || checkUserId.value?.planId === 'P00001')
const isPaid = computed(() => isLoggedIn.value && checkUserId.value?.planId !== 'P00001')

/* ===== 카테고리 ===== */
// const categories = reactive({
//   generic: [
//     { code: '1', label: 'Access' }, { code: '2', label: 'Authorization' }, { code: '3', label: 'Containment' },
//     { code: '4', label: 'Limit' }, { code: '5', label: 'Procedures' }, { code: '6', label: 'Segregate' },
//     { code: '7', label: 'Standards' }, { code: '8', label: 'Void' }, { code: '9', label: 'Accounting' },
//     { code: '10', label: 'Confirm' }, { code: '11', label: 'Compliance' }, { code: '12', label: 'Identify' },
//     { code: '13', label: 'Simulate' }, { code: '14', label: 'Observe' }, { code: '15', label: 'Redundancy' },
//     { code: '16', label: 'Feedback' }, { code: '17', label: 'Sample' }, { code: '18', label: 'Tag' },
//     { code: '19', label: 'Verify' }, { code: '20', label: 'Scan' }, { code: '21', label: 'Copy' },
//     { code: '22', label: 'Defaults' }, { code: '23', label: 'Prompt' }, { code: '24', label: 'Retrain' },
//   ],
//   usage: [
//     { code: '1', label: 'Origination' }, { code: '2', label: 'Entry' }, { code: '3', label: 'Communications' },
//     { code: '4', label: 'Process' }, { code: '5', label: 'Storage (on-line)' }, { code: '6', label: 'Storage (off-line)' },
//     { code: '7', label: 'Output' }, { code: '8', label: 'Use of Data' }, { code: '9', label: 'General Purpose' },
//     { code: '0', label: 'Authorization' },
//   ],
//   strength: [
//     { code: 'A', label: 'Very Strong' }, { code: 'B', label: 'Strong' },
//     { code: 'C', label: 'Average' }, { code: 'D', label: 'Minimal' },
//   ],
//   type: [
//     { code: 'P', label: 'Preventive' }, { code: 'D', label: 'Detective' }, { code: 'C', label: 'Corrective' },
//   ],
// })

const categories = reactive({
  generic: [
    { code: '1', label: '접근 관리' },
    { code: '2', label: '승인' },
    { code: '3', label: '보관/보호' },
    { code: '4', label: '제한' },
    { code: '5', label: '절차' },
    { code: '6', label: '작업 분리' },
    { code: '7', label: '기준' },
    { code: '8', label: '무효' },
    { code: '9', label: '회계' },
    { code: '10', label: '확인' },
    { code: '11', label: '준수' },
    { code: '12', label: '식별' },
    { code: '13', label: '시뮬레이션' },
    { code: '14', label: '관찰' },
    { code: '15', label: '중복' },
    { code: '16', label: '피드백' },
    { code: '17', label: '표본' },
    { code: '18', label: '태그' },
    { code: '19', label: '검증' },
    { code: '20', label: '스캔' },
    { code: '21', label: '사본' },
    { code: '22', label: '기본값' },
    { code: '23', label: '프롬프트' },
    { code: '24', label: '보존' },
  ],

  usage: [
    { code: '1', label: '생성' },
    { code: '2', label: '입력' },
    { code: '3', label: '커뮤니케이션' },
    { code: '4', label: '프로세스' },
    { code: '5', label: '저장(온라인)' },
    { code: '6', label: '저장(오프라인)' },
    { code: '7', label: '산출물' },
    { code: '8', label: '데이터 활용' },
    { code: '9', label: '일반 목적' },
    { code: '0', label: '권한 관리' },
  ],

  strength: [
    { code: 'A', label: '매우 강함' },
    { code: 'B', label: '강함' },
    { code: 'C', label: '보통' },
    { code: 'D', label: '최소' },
  ],

  type: [
    { code: 'P', label: '예방 통제' },
    { code: 'D', label: '적발 통제' },
    { code: 'C', label: '시정 통제' },
  ],
})


/* ===== 상태 ===== */
const selected = reactive({ generic: '', usage: '', strength: '', type: '' })
const available = reactive({ usage: new Set(), strength: new Set(), type: new Set() })

const showDoc = ref(false)        // 유료용 실제 문서
const docs = ref([])              // 실제 문서 리스트
const showSampleDoc = ref(false)  // 무료용 샘플 문서
const hint = ref('')

/* ===== 계층 호출 ===== */
async function fetchChildren(referenceCode) {
  try {
    const res = await referencechildren({ referenceCode })
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    return list.map(v => (typeof v === 'string' ? v : v.code)).filter(Boolean)
  } catch {
    return []
  }
}

async function onPickGeneric() {
  selected.usage = selected.strength = selected.type = ''
  available.strength = new Set()
  available.type = new Set()
  available.usage = new Set(await fetchChildren(`${selected.generic}.`))

  hint.value = available.usage.size ? '' : '선택 가능한 Usage가 없습니다.'
  showDoc.value = false
  showSampleDoc.value = false
  docs.value = []
}

async function onPickUsage() {
  selected.strength = selected.type = ''
  available.type = new Set()
  if (!selected.generic) return
  available.strength = new Set(await fetchChildren(`${selected.generic}.${selected.usage}.`))

  hint.value = available.strength.size ? '' : '선택 가능한 Strength가 없습니다.'
  showDoc.value = false
  showSampleDoc.value = false
  docs.value = []
}

async function onPickStrength() {
  selected.type = ''
  if (!selected.generic || !selected.usage) return
  available.type = new Set(await fetchChildren(`${selected.generic}.${selected.usage}.${selected.strength}.`))

  hint.value = available.type.size ? '' : '선택 가능한 통제 유형이 없습니다.'
  showDoc.value = false
  showSampleDoc.value = false
  docs.value = []
}

/* ===== 조회 ===== */
async function onSearch() {
  // 선택값 검증은 무료/유료 공통
  if (!selected.generic) return void (hint.value = '통제 분류를 선택해주세요.')
  if (!selected.usage) return void (hint.value = '활용 활동 영역을 선택해주세요.')
  if (!selected.strength) return void (hint.value = '통제 강도를 선택해주세요.')
  if (!selected.type) return void (hint.value = '통제 유형을 선택해주세요.')

  // 🔹 무료/비로그인: 샘플 문서만 노출
  if (isFree.value) {
    alert(
      '현재 무료 플랜에서는 샘플 문서만 조회할 수 있습니다.\n' +
      '자세한 문서 열람을 원하시면 문의 주세요.\n' +
      '문의: 070-4489-7447'
    )
    hint.value = ''
    showSampleDoc.value = true
    showDoc.value = false
    docs.value = []
    return
  }

  // 🔹 유료: 기존처럼 실제 데이터 조회
  try {
    const referenceCode = `${selected.generic}.${selected.usage}.${selected.strength}.${selected.type}`
    const res = await selectLibrary({ referenceCode })
    const list = Array.isArray(res?.data)
      ? res.data
      : (Array.isArray(res) ? res : (res ? [res] : []))

    if (!list.length) {
      hint.value = '해당 코드의 문서가 없습니다.'
      showDoc.value = false
      docs.value = []
      showSampleDoc.value = false
      return
    }

    docs.value = list
    showDoc.value = true
    showSampleDoc.value = false
    hint.value = ''
  } catch (e) {
    hint.value = '조회 중 오류가 발생했습니다.'
    showDoc.value = false
    showSampleDoc.value = false
    docs.value = []
  }
}
</script>


<style scoped>
.archive-tree {
  font-family: 'Noto Sans KR', sans-serif;
  padding: 24px;
  background: #fff;
  color: #111;
  line-height: 1.7;
  max-width: 1200px;
  margin: 0 auto;
}

.title {
  font-size: 1.625rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.desc p {
  font-size: 0.95rem;
  margin-bottom: 12px;
  word-break: keep-all;
}


.usage-guide-wrap {
  font-weight: 700;
  font-size: 1.25rem;
}

.usage-guide {
  margin-top: 12px;
  font-size: 0.95rem;
  line-height: 1.75;
}

.usage-guide ol {
  list-style: none;
  padding-left: 0;
  counter-reset: step;
}

.usage-guide li {
  margin-bottom: 18px;
}

.usage-guide strong {
  display: inline-block;
  font-weight: 700;
  color: #0f172a;
}

.usage-guide em {
  display: block;
  margin-top: 4px;
  color: #555;
  font-style: normal;
  font-size: 0.9rem;
}

/* 조회 영역 */
.search-fixed {
  /* position: sticky; */
  bottom: 0;
  background: #fff;
  padding: 16px 0;
  text-align: center;
  z-index: 10;
}

.btn-search {
  font-size: 1rem;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 8px;
  border: 1px solid #30308a;
  background: #30308a;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}


/* ✅ 항상 hover/active만 유지, disabled 관련 제거 */
.btn-search:hover {
  background: #30308a;
}

.btn-search:active {
  transform: scale(0.97);
}

.hint-msg {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #b91c1c;
  /* 붉은 톤 */
}

.hint {
  margin-top: 8px;
  color: #ef4444;
}

.selector {
  max-width: 980px;
  margin: 40px auto;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
}

.group {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.group h3 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.group label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.95rem;
}


/* ===== 기본 타이포 ===== */
:where(*) {
  font-family: 'Noto Sans KR', sans-serif;
}

.title1 {
  font-size: 1.625rem;
  font-weight: 700;
}

p {
  font-size: 0.95rem;
}

/* ===== 레이아웃 ===== */
.control-sheet {
  max-width: 820px;
  margin: 24px auto;
  padding: 16px;
  background: #fff;
}

.title1 {
  text-align: center;
  margin: 28px 0 14px;
}

/* ===== 표(그리드) - 내부 보더 정리 버전 ===== */
.sheet-grid {
  border: 1px solid #888;
  /* 바깥 테두리 */
}

/* 행 기본: 보더는 '셀'에만 준다 (겹침 방지) */
.row {
  display: grid;
  grid-template-columns: 200px 1fr;
}

/* 모든 셀의 기본 보더는 위쪽만 */
.row>* {
  padding: 12px 14px;
  min-height: 54px;
  border-top: 1px solid #888;
  /* 행 사이 구분선 */
}

.row2 {
  min-height: 200px;
}

.th2{
  align-content: center;
}

.td2{
  white-space: pre-line;
  word-break: keep-all;
}

/* 첫 번째 행은 위쪽 선 제거 (바깥 테두리와 겹치지 않게) */
.sheet-grid>.row:first-child>* {
  border-top: none;
}

/* 각 행에서 첫 번째 셀을 제외한 나머지 셀에 왼쪽 선 */
.row>*:not(:first-child) {
  border-left: 1px solid #888;
  /* 열 구분선 */
}

/* 헤더 셀 꾸밈 */
.th {
  font-size: 0.9rem;
  font-weight: 700;
  background: #f7f7f7;
}

/* 첫 행: Ref + Name 나란히 (4열) */
.row--head {
  grid-template-columns: 200px 1fr 200px 1fr;
  align-content: center;
}

.row--head .ref-code,
.row--head .name {
  font-weight: 700;
}

/* 메타 정보 4쌍 (4열) */
.row--meta {
  grid-template-columns: 200px 1fr 200px 1fr;
}

/* ===== 반응형 ===== */
@media (max-width: 720px) {
  .row {
    grid-template-columns: 1fr;
  }

  /* 한 열로 쌓기 */
  .row>*:not(:first-child) {
    border-left: none;
  }

  /* 한 열일 땐 좌측선 제거 */
  .th {
    border-bottom: 1px solid #888;
  }

  /* 라벨과 값 사이 분리감 */
}

/* 무료 안내 */
.guard-note {
  margin: 8px 0 16px;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
}

/* STRENGTH 셀 왼쪽 줄 제거 */
.row--meta .th:nth-of-type(5) {
  border-left: none !important;
}


/* ===========================
   문서 영역 전용 테마 오버라이드
   =========================== */

/* 문서 카드 */
.control-sheet {
  max-width: 1200px;
  margin: 24px auto;
  padding: 24px;
  background: #fcfcfc;
  border: 1px solid #30308a;
  /* 메인 블루 라인 */
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* 상단 타이틀을 헤더 바처럼 */
.title1 {
  text-align: center;
  margin: 0 0 18px;
  padding: 14px 48px;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  background: #ffffff;
  letter-spacing: .02em;
  color: #333;
  font-size: 1.625rem;
  /* 지시사항 유지 */
  font-weight: 700;
}

/* 표(그리드) – 연블루 톤 */
.sheet-grid {
  border: 1px solid #e4ecf3;
  /* 바깥 테두리 연블루 */
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  /* 라운드 유지 */
}

/* 행 기본: 보더는 셀에만 */
.row>* {
  border-top: 1px solid #e4ecf3;
  /* 행 구분선 연블루 */
  padding: 12px 14px;
  min-height: 54px;
}

/* 첫 행은 위쪽선 제거 */
.sheet-grid>.row:first-child>* {
  border-top: none;
  align-content: center;
}

/* 열 구분선 */
.row>*:not(:first-child) {
  border-left: 1px solid #e4ecf3;
  align-content: center;
}

/* 헤더 셀: 연블루 배경 + 딥블루 텍스트 */
.th {
  background: #f1f6fb;
  color: #2b4a5a;
  font-size: 0.9rem;
  font-weight: 700;
}

/* 4열 행(Ref/Name, 메타) 구조 유지 */
.row--head {
  grid-template-columns: 200px 1fr 200px 1fr;
}

.row--meta {
  grid-template-columns: 200px 1fr 200px 1fr;
}

.row--head .ref-code,
.row--head .name {
  font-weight: 700;
}

/* 짝수 데이터 셀 살짝 톤 차이 */
.row:not(.row--head):not(.row--meta) .td {
  background: #fff;
}

/* .row:nth-child(even) .td { background: #fcfcfc;} */

/* STRENGTH 셀 왼쪽 줄 제거(요청 유지) */
.row--meta .th:nth-of-type(5) {
  border-left: none !important;
}

/* 반응형 */
@media (max-width: 720px) {
  .row {
    grid-template-columns: 1fr;
  }

  .row>*:not(:first-child) {
    border-left: none;
  }

  .th {
    border-bottom: 1px solid #e4ecf3;
  }
}
</style>
